import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { CoinListItem } from '../../types';
import { cgGet } from '../../utils/coingecko';
import { saveCoinImage } from '../../utils/coinImageCache';
import { isMetal, METAL_DISPLAY } from '../../utils/metals';
import { isStockOrETF, isETF, yahooTicker, SUPPORTED_STOCK_ITEMS } from '../../utils/stocks';
import { fetchStockSpot } from '../../hooks/useStockPrices';
import { ChevronDown, Search, Loader2, AlertCircle, TrendingUp } from 'lucide-react';
import { isNarrowViewport } from '../../utils/viewport';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { useCurrency } from '../../context/CurrencyContext';
import { loadCoinImages } from '../../utils/coinImageCache';

interface CoinSelectorProps {
  coins: CoinListItem[];
  metals?: CoinListItem[];
  selectedId: string;
  onSelect: (coin: CoinListItem) => void;
  loading: boolean;
  error: string | null;
  fallbackCoin?: CoinListItem;
  /**
   * When provided, the selector is locked to exactly this set of assets (used by
   * the SELL form to restrict to coins the user holds). Network search is
   * disabled, and an empty array renders an inline empty state.
   */
  restrictTo?: CoinListItem[];
  /** Message shown when `restrictTo` is an empty array. */
  emptyMessage?: string;
}

/**
 * How many local (top-200) matches count as "enough" to skip the network search.
 *
 * Zero was the old bar, and it made most of CoinGecko unreachable: the network
 * search only ran when the local list matched NOTHING, so any query sharing a
 * prefix with a top-200 coin blocked it. Typing "bit" matched Bitcoin, so the
 * search never fired and "Bitkub Coin" could not be found at all — the universe
 * was uncapped in principle and unreachable in practice.
 *
 * A small non-zero bar fixes that while keeping the common case free: searching
 * for a mainstream coin still returns plenty of local matches and costs no
 * request.
 */
const LOCAL_MATCH_THRESHOLD = 5;

interface CgSearchResult {
  id: string;
  name: string;
  symbol: string;
  market_cap_rank: number | null;
  thumb: string;
  large: string;
}

// ── Tokenized-equity filter ───────────────────────────────────────────────────
// CoinGecko lists crypto tokens that imitate real stocks — e.g. "NVIDIA xStock",
// "NVIDIA (Ondo Tokenized Stock)", "NVIDIA (bStocks Tokenized Stock)", "NVIDIA -
// Robinhood Token", "NVIDIA rStock". They are NOT the real equity and carry wrong
// prices / tax treatment, so we hide them from the crypto results — the genuine
// stock comes from the Yahoo path (id `stock:NVDA`). Markers are word-boundary
// anchored so legitimate crypto (e.g. a token named "SuperStock") is never caught.
// Add new provider markers here to extend.
const TOKENIZED_EQUITY_RE =
  /\b(?:tokenized|synthetic|wrapped)\s+(?:stock|equity)\b|\brobinhood token\b|\bondo tokenized\b|\b[xrb]stocks?\b/i;

function isTokenizedEquity(c: { name: string; symbol: string }): boolean {
  return TOKENIZED_EQUITY_RE.test(`${c.name} ${c.symbol}`);
}

// ── Icon components ──────────────────────────────────────────────────────────

function MetalCircle({ coinId, size = 'w-7 h-7' }: { coinId: string; size?: string }) {
  const d = METAL_DISPLAY[coinId];
  if (!d) return null;
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center font-bold flex-shrink-0`}
      style={{ backgroundColor: d.bg, border: `1.5px solid ${d.border}` }}
    >
      <span style={{ color: d.color, fontSize: '10px' }}>{d.label}</span>
    </div>
  );
}

function StockCircle({ coinId, size = 'w-7 h-7' }: { coinId: string; size?: string }) {
  const etf   = isETF(coinId);
  const color = etf ? '#8B5CF6' : '#3B82F6';
  const bg    = etf ? '#8B5CF615' : '#3B82F615';
  const bdr   = etf ? '#8B5CF640' : '#3B82F640';
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: bg, border: `1.5px solid ${bdr}` }}
    >
      <TrendingUp style={{ color, width: '55%', height: '55%' }} />
    </div>
  );
}

function AssetIcon({ coin, size }: { coin: CoinListItem; size?: string }) {
  if (isMetal(coin.id))       return <MetalCircle coinId={coin.id} size={size} />;
  if (isStockOrETF(coin.id))  return <StockCircle coinId={coin.id} size={size} />;
  return (
    <img
      src={coin.image}
      alt={coin.name}
      className={`${size ?? 'w-7 h-7'} rounded-full flex-shrink-0 bg-slate-700`}
      loading="lazy"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

// ── List rows & section header ────────────────────────────────────────────────

function CoinRow({
  coin,
  isSelected,
  onSelect,
  showPrice = true,
}: {
  coin: CoinListItem;
  isSelected: boolean;
  onSelect: (c: CoinListItem) => void;
  showPrice?: boolean;
}) {
  const { fmt } = useCurrency();
  return (
    <button
      type="button"
      onClick={() => onSelect(coin)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[rgb(var(--overlay)/0.05)] ${isSelected ? 'bg-[rgb(var(--overlay)/0.09)]' : ''}`}
    >
      {/* Rank / exchange badge */}
      {coin.exchange ? (
        <span className="text-[10px] text-slate-600 w-10 text-right flex-shrink-0 font-mono leading-tight truncate">
          {coin.exchange}
        </span>
      ) : (
        <span className="text-xs text-slate-600 w-6 text-right flex-shrink-0 font-mono">
          {coin.marketCapRank > 0 ? coin.marketCapRank : '—'}
        </span>
      )}

      <AssetIcon coin={coin} size="w-7 h-7" />

      {/* Name + symbol */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${isSelected ? 'text-slate-100' : 'text-slate-100'}`}>
            {coin.symbol}
          </span>
          {isSelected && (
            <span className="text-xs bg-[rgb(var(--overlay)/0.12)] text-slate-100 px-1.5 py-0.5 rounded-full">selected</span>
          )}
          {isETF(coin.id) && !isSelected && (
            <span className="text-[10px] bg-[rgb(var(--overlay)/0.06)] text-slate-400 px-1 py-0.5 rounded font-medium">ETF</span>
          )}
        </div>
        <span className="text-xs text-slate-400 truncate block">{coin.name}</span>
      </div>

      {/* currentPrice is USD (useCoinList fetches vs_currency=usd), so it goes
          through fmt(), which converts at today's rate and prefixes the selected
          currency. Printing it with a bare "$" showed a USD figure labelled as
          though it were the user's own currency. */}
      {showPrice && coin.currentPrice > 0 && (
        <span className="text-xs text-slate-500 flex-shrink-0 font-mono">
          {fmt(coin.currentPrice)}
        </span>
      )}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5 bg-slate-900/60 border-b border-slate-700/50">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{children}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CoinSelector({ coins, metals = [], selectedId, onSelect, loading, error, fallbackCoin, restrictTo, emptyMessage }: CoinSelectorProps) {
  const restricted = restrictTo !== undefined;
  const [open,             setOpen]             = useState(false);
  const [query,            setQuery]            = useState('');
  const [searchResults,    setSearchResults]    = useState<CoinListItem[]>([]);
  const [searching,        setSearching]        = useState(false);
  const [fetchingPrice,    setFetchingPrice]    = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef    = useRef<HTMLInputElement>(null);

  // Defaults to [] outside a PortfolioProvider, so the menu degrades to its
  // previous behaviour rather than breaking wherever it is mounted.
  const { holdings } = usePortfolioContext();
  const imageCache = useMemo(() => loadCoinImages(), []);
  const listRef      = useRef<HTMLDivElement>(null);

  // Look up the selected coin in all sources, with optional fallback for pre-filled edits
  const selected =
    (restrictTo?.find((c) => c.id === selectedId)) ??
    coins.find((c) => c.id === selectedId) ??
    metals.find((m) => m.id === selectedId) ??
    SUPPORTED_STOCK_ITEMS.find((s) => s.id === selectedId) ??
    (fallbackCoin?.id === selectedId ? fallbackCoin : null) ??
    null;

  const selectedDisplayText = selected ? `${selected.name} (${selected.symbol})` : '';

  // ── Close handlers ──
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Auto-focus on open (desktop only) ──
  //
  // On a phone, focusing the search box as the menu opens raises the keyboard
  // over most of the list, so the first thing the user sees is the thing they
  // were trying to browse being covered. Opening the menu is a request to LOOK;
  // typing is a separate decision, and tapping the search box still makes it.
  // Desktop keeps the autofocus, where there is no keyboard to get in the way
  // and open-and-type is the fast path.
  //
  // Checked at open time rather than on mount so a resize or rotation cannot
  // leave a stale answer behind.
  useEffect(() => {
    if (open) {
      setQuery(selected ? selectedDisplayText : '');
      setSearchResults([]);
      if (isNarrowViewport()) return;
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
          searchRef.current.select();
        }
      }, 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isShowingSelection = Boolean(selected) && query === selectedDisplayText;

  /**
   * What the user actually owns, richest first.
   *
   * The lists below this one are the top 200 by market cap plus a curated stock
   * set, so an asset held outside both was previously unreachable from this
   * menu even though the user owns it. Holdings come from PortfolioContext,
   * which every call site already sits inside; outside a provider it defaults to
   * an empty array, so the menu renders exactly as it did before.
   *
   * Not built in restricted mode: `restrictTo` IS the held set there, and the
   * SELL form already labels it.
   */
  const heldItems = useMemo<CoinListItem[]>(() => {
    if (restricted) return [];
    const byId = new Map<string, CoinListItem>();
    for (const c of coins) byId.set(c.id, c);
    for (const m of metals) byId.set(m.id, m);
    for (const s of SUPPORTED_STOCK_ITEMS) byId.set(s.id, s);
    return holdings
      .filter((h) => h.amount > 0)
      .slice()
      .sort((a, b) => b.totalValue - a.totalValue)
      .map((h) => {
        const known = byId.get(h.coinId);
        const base: CoinListItem = known ?? {
          id: h.coinId, symbol: h.symbol, name: h.name,
          image: imageCache[h.coinId] ?? '',
          marketCapRank: 0, currentPrice: 0, marketCap: 0,
          volume24h: 0, high24h: 0, low24h: 0,
        };
        // The portfolio's live price beats the list snapshot, and is the only
        // price there is for anything outside the top 200.
        return { ...base, currentPrice: h.currentPrice > 0 ? h.currentPrice : base.currentPrice };
      });
  }, [restricted, holdings, coins, metals, imageCache]);

  const heldIds = useMemo(() => new Set(heldItems.map((c) => c.id)), [heldItems]);

  const filteredHeld = useMemo(() => {
    if (!query.trim() || isShowingSelection) return heldItems;
    const q = query.toLowerCase();
    return heldItems.filter((c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q));
  }, [heldItems, query, isShowingSelection]);

  // ── Restricted set (SELL: coins the user holds) — locally filtered only ──
  const filteredRestricted = useMemo(() => {
    const list = restrictTo ?? [];
    if (!query.trim() || isShowingSelection) return list;
    const q = query.toLowerCase();
    return list.filter((c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q));
  }, [restrictTo, query, isShowingSelection]);

  // ── Local crypto filter (top-200) — tokenized-equity lookalikes excluded ──
  const filtered = useMemo(() => {
    // heldIds removes anything already listed under Your portfolio above.
    const source = coins.filter((c) => !isTokenizedEquity(c) && !heldIds.has(c.id));
    if (!query.trim() || isShowingSelection) return source;
    const q = query.toLowerCase();
    return source.filter(
      (c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q)
    );
  }, [coins, query, isShowingSelection, heldIds]);

  // ── Metal filter ──
  const filteredMetals = useMemo(() => {
    const source = metals.filter((m) => !heldIds.has(m.id));
    if (!source.length) return source;
    if (!query.trim() || isShowingSelection) return source;
    const q = query.toLowerCase();
    return source.filter((m) => m.name.toLowerCase().includes(q) || m.symbol.toLowerCase().includes(q));
  }, [metals, query, isShowingSelection, heldIds]);

  // ── Stock/ETF filter (curated list, local — no network search) ──
  const filteredStocks = useMemo(() => {
    if (restricted) return [];
    // A held stock is already listed under Your portfolio above.
    const source = SUPPORTED_STOCK_ITEMS.filter((s) => !heldIds.has(s.id));
    if (!query.trim() || isShowingSelection) return source;
    const q = query.toLowerCase();
    return source.filter(
      (s) => s.name.toLowerCase().includes(q) || s.symbol.toLowerCase().includes(q)
    );
  }, [restricted, query, isShowingSelection, heldIds]);

  // ── Debounced CoinGecko /search (only when top-200 has no matches) ──
  useEffect(() => {
    const q = query.trim();
    setSearchResults([]);
    if (restricted) { setSearching(false); return; }
    // Run whenever the local list is THIN, not only when it is empty.
    if (filtered.length >= LOCAL_MATCH_THRESHOLD || q.length < 2) { setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await cgGet('/search', { params: { query: q }, timeout: 8_000 });
        const raw: CgSearchResult[] = ((data as any).coins as CgSearchResult[]) ?? [];
        setSearchResults(
          raw
            .slice(0, 30)
            .map((r) => ({
              id: r.id, symbol: r.symbol.toUpperCase(), name: r.name,
              image: r.large || r.thumb, marketCapRank: r.market_cap_rank ?? 0,
              currentPrice: 0, marketCap: 0, volume24h: 0, high24h: 0, low24h: 0,
            }))
            // Drop tokenized-stock imitations so they can't be mistaken for the real stock.
            .filter((c) => !isTokenizedEquity(c))
        );
      } catch { setSearchResults([]); }
      finally  { setSearching(false); }
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filtered.length]);

  /**
   * Network results minus anything already listed above them.
   *
   * Now that the search runs alongside a non-empty local list, the two sets can
   * overlap — CoinGecko's /search returns top-200 coins too. Without this, a
   * thin query would show the same coin twice, once under its local heading and
   * again under "More results".
   */
  const mergedSearchResults = useMemo(() => {
    if (!searchResults.length) return searchResults;
    const alreadyShown = new Set(filtered.map((c) => c.id));
    return searchResults.filter((c) => !alreadyShown.has(c.id) && !heldIds.has(c.id));
  }, [searchResults, filtered, heldIds]);

  // ── Select handler: fetch live price for stocks before notifying parent ──
  const handleSelect = useCallback(async (coin: CoinListItem) => {
    let final = coin;

    if (isStockOrETF(coin.id) && coin.currentPrice === 0) {
      setFetchingPrice(true);
      setOpen(false);
      setQuery('');
      try {
        const { usd } = await fetchStockSpot(yahooTicker(coin.id));
        if (usd > 0) final = { ...coin, currentPrice: usd };
      } catch (err) {
        // Graceful fallback: keep price 0 so the user can type it manually — but
        // don't hide the failure. Log it so a broken proxy is visible in dev.
        console.error('[CoinSelector] stock price fetch failed for', coin.id, err);
      }
      finally { setFetchingPrice(false); }
    } else if (!isMetal(coin.id) && coin.currentPrice === 0) {
      // An off-list crypto, i.e. one that came from /search rather than the
      // top-200 list — those arrive with currentPrice 0 because /search returns
      // no prices. Without this the price field does not prefill and the
      // "Current: …" helper stays hidden, so a coin found by search felt broken
      // next to one picked from the list.
      //
      // Only ever fires for a coin with no price: everything in the top-200 list
      // already has one, so the common path costs no extra request.
      setFetchingPrice(true);
      setOpen(false);
      setQuery('');
      try {
        const { data } = await cgGet('/simple/price', {
          params: { ids: coin.id, vs_currencies: 'usd' },
          timeout: 8_000,
        });
        const usd = (data as Record<string, { usd?: number }>)?.[coin.id]?.usd ?? 0;
        if (usd > 0) final = { ...coin, currentPrice: usd };
      } catch (err) {
        // Same fallback as the stock path: keep price 0 so the user can type it,
        // but log it so a broken proxy is visible rather than silent.
        console.error('[CoinSelector] crypto price fetch failed for', coin.id, err);
      }
      finally { setFetchingPrice(false); }
    } else {
      setOpen(false);
      setQuery('');
    }

    if (final.image) saveCoinImage(final.id, final.image);
    onSelect(final);
    setSearchResults([]);
  }, [onSelect]);

  // ── Derived booleans ──
  const hasRestricted = filteredRestricted.length > 0;
  const hasHeld    = filteredHeld.length > 0;
  const hasMetals  = filteredMetals.length > 0;
  const hasTop200  = filtered.length > 0;
  const hasMore    = mergedSearchResults.length > 0;
  const hasStocks  = filteredStocks.length > 0;
  /** Anything at all already rendered above the network results. */
  const hasAnyLocal = hasHeld || hasMetals || hasTop200 || hasStocks;
  const showEmpty  = !hasHeld && !hasMetals && !hasTop200 && !hasMore && !hasStocks &&
                     !searching && query.trim().length > 0 && !isShowingSelection;
  const showRestrictedEmpty = restricted && !hasRestricted && !isShowingSelection;
  const showDefault = !query.trim();

  // ── Trigger button label ──
  const triggerLabel = fetchingPrice ? 'Fetching price…' : null;

  // Restricted to an empty set (e.g. SELL with no holdings): inline empty state.
  if (restricted && (restrictTo?.length ?? 0) === 0) {
    return (
      <div className="w-full flex items-center bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-500">
        {emptyMessage ?? 'No assets available.'}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !fetchingPrice && setOpen((o) => !o)}
        disabled={(loading && coins.length === 0) || fetchingPrice}
        className="w-full flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-left hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors disabled:opacity-50"
      >
        {loading && coins.length === 0 ? (
          <>
            <Loader2 className="w-4 h-4 text-slate-500 animate-spin flex-shrink-0" />
            <span className="text-slate-500 flex-1">Loading coins…</span>
          </>
        ) : fetchingPrice ? (
          <>
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
            <span className="text-slate-400 flex-1">{triggerLabel}</span>
          </>
        ) : selected ? (
          <>
            <AssetIcon coin={selected} size="w-6 h-6" />
            <span className="font-semibold text-slate-100 flex-shrink-0">{selected.symbol}</span>
            <span className="text-slate-400 truncate flex-1">{selected.name}</span>
          </>
        ) : (
          <span className="text-slate-500 flex-1">{restricted ? 'Select a coin to sell…' : 'Select an asset…'}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden">

          {/* Search bar */}
          <div className="p-2 border-b border-slate-700">
            <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-2">
              {isShowingSelection && selected ? (
                <AssetIcon coin={selected} size="w-4 h-4" />
              ) : searching ? (
                <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin flex-shrink-0" />
              ) : (
                <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              )}
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={restricted ? 'Search your holdings…' : 'Search crypto, stocks (AAPL), ETFs (SPY)…'}
                // 16px minimum: anything smaller and a mobile browser zooms the
                // page on focus, leaving the menu scrolled off to one side.
                className="flex-1 bg-transparent text-sm max-md:text-[16px] text-slate-100 placeholder-slate-500 focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setSearchResults([]); }}
                  className="text-slate-500 hover:text-slate-300 text-xs leading-none"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Coin list cache error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-yellow-400 bg-yellow-500/5 border-b border-slate-700">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Results */}
          {/* Sized in dvh below md so the list is measured against what the
              browser actually leaves visible, keyboard and toolbars included,
              and stays scrollable rather than running on behind them. */}
          <div ref={listRef} className="overflow-y-auto max-h-80 max-md:max-h-[45dvh]">

            {/* Restricted set (SELL: only coins the user holds) */}
            {restricted && (
              <>
                {hasRestricted && (
                  <>
                    <SectionLabel>Your Holdings</SectionLabel>
                    {filteredRestricted.map((coin) => (
                      <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice />
                    ))}
                  </>
                )}
                {showRestrictedEmpty && (
                  <div className="px-4 py-6 text-center text-sm text-slate-500">
                    No holdings match &ldquo;{query}&rdquo;
                  </div>
                )}
              </>
            )}

            {/* The user's own assets first. Same heading and ordering as
                AssetPicker's, so the two menus read the same way. */}
            {!restricted && hasHeld && (
              <>
                <SectionLabel>Your portfolio</SectionLabel>
                {filteredHeld.map((coin) => (
                  <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice />
                ))}
              </>
            )}

            {/* Precious metals */}
            {!restricted && hasMetals && (
              <>
                <SectionLabel>Precious Metals</SectionLabel>
                {filteredMetals.map((coin) => (
                  <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice />
                ))}
              </>
            )}

            {/* Top-200 crypto */}
            {!restricted && hasTop200 && (
              <>
                {/* The heading used to be hidden in the default view because the
                    top-200 list WAS the whole menu. With Your portfolio above it
                    the unlabelled block reads as more of the user's holdings, so
                    it is now also shown whenever that section is present. */}
                {(!showDefault || hasHeld) && <SectionLabel>Top 200 Crypto</SectionLabel>}
                {filtered.map((coin) => (
                  <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice />
                ))}
              </>
            )}

            {/* Stocks & ETFs — a curated list of major assets, surfaced ABOVE the
                extended crypto results so the genuine equity/ETF is easy to find
                (and can't be confused with CoinGecko's tokenized-stock lookalikes). */}
            {!restricted && hasStocks && (
              <>
                <SectionLabel>Stocks &amp; ETFs</SectionLabel>
                {filteredStocks.map((coin) => (
                  <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice={false} />
                ))}
              </>
            )}

            {/* CoinGecko extended search */}
            {!restricted && hasMore && (
              <>
                <SectionLabel>More Crypto</SectionLabel>
                {mergedSearchResults.map((coin) => (
                  <CoinRow key={coin.id} coin={coin} isSelected={coin.id === selectedId} onSelect={handleSelect} showPrice={false} />
                ))}
              </>
            )}

            {/* CoinGecko searching spinner — nothing on screen yet, so it takes
                the whole space and reads as the list's state. */}
            {!restricted && searching && !hasAnyLocal && !hasMore && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                Searching CoinGecko…
              </div>
            )}

            {/* The same search, but with local matches already listed above it.
                Now that the search runs on a THIN local list rather than only an
                empty one, this is the common case — without a trailing hint the
                list looks settled and then silently grows a section half a second
                later. Compact, and sits where "More Crypto" will appear. */}
            {!restricted && searching && hasAnyLocal && !hasMore && (
              <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-slate-500 border-t border-slate-700/40">
                <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                Searching CoinGecko for more…
              </div>
            )}

            {/* Empty state */}
            {!restricted && showEmpty && !isShowingSelection && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No results for &ldquo;{query}&rdquo;
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-slate-700 bg-slate-900/50">
            <p className="text-xs text-slate-600 text-center">
              {restricted
                ? `${(restrictTo?.length ?? 0)} coin${(restrictTo?.length ?? 0) !== 1 ? 's' : ''} you hold`
                : showDefault
                ? `${filtered.length} of ${coins.length} coins · Top 200 by market cap`
                : hasStocks && !hasMore
                ? `${filteredStocks.length} stock${filteredStocks.length !== 1 ? 's' : ''}/ETFs · curated`
                : hasMore
                  ? `${mergedSearchResults.length} results from CoinGecko`
                  : `${filtered.length} of ${coins.length} coins · Top 200 by market cap`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
